import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Minus, PackagePlus, Plus, Truck, X,
} from 'lucide-react';

import { changeOfficeStock, dispatchCarrierShipment, loadShipmentStock } from './api';
import { orderReferenceLabel } from './labels';
import { SearchField, useLeave } from './parts';
import { FilterMenu } from './ProductFilter';
import { errorMessage } from './ui';
import { tx } from './i18n';
import './products.css';

// An office's stock of a product, in the Stock tab of the product's sheet
// (XomicsStoreProducts.js). How many there are is never typed: it is added to
// or taken from, each time for a reason, and a dispatched shipment takes what
// it holds. Every change stays in the history beside the number, with who made
// it and when.

const when = (at) => new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const signed = (quantity) => `${quantity > 0 ? '+' : '−'}${Math.abs(quantity).toLocaleString()}`;
// The kinds a history is narrowed to; the first is every change.
const KINDS = ['', 'added', 'removed', 'shipped'];
const KIND_LABEL = {
  '': 'ui.store.stock.kind.all', added: 'ui.store.stock.kind.added', removed: 'ui.store.stock.kind.removed', shipped: 'ui.store.stock.kind.shipped',
};

// The two ways a person changes a stock, and for an office a third: asking a
// warehouse for more (`onRequest`).
function Changes({ onChange, onRequest = null }) {
  const button = (adds, Icon, label) => (
    <button type="button" className="xo-btn-2 xo-stock-change" onClick={() => onChange(adds)}>
      <Icon aria-hidden="true" />{label}
    </button>
  );
  return (
    <span className="xo-stock-changes">
      {button(true, Plus, tx('ui.store.stock.add'))}
      {button(false, Minus, tx('ui.store.stock.remove'))}
      {onRequest && (
        <button type="button" className="xo-btn-2 xo-stock-change" onClick={onRequest}>
          <PackagePlus aria-hidden="true" />{tx('ui.stock_requests.request_more')}
        </button>
      )}
    </span>
  );
}

// How a product's combinations are laid out. With two choices: a table, the
// one with the longer names down the side and the other across (a ring's
// finishes down, its sizes across). With one choice, or more than two: a
// list, a row each.
function layoutOf(stock) {
  const [first, second, ...more] = stock.choices ?? [];
  if (!second || more.length > 0) return null;
  const widest = (choice) => Math.max(0, ...choice.values.map((value) => value.label.length));
  const [down, across] = widest(first) >= widest(second) ? [first, second] : [second, first];
  const placed = new Map(stock.combinations.map((row) => [`${row.selection[down.code]}|${row.selection[across.code]}`, row]));
  return { down, across, at: (side, top) => placed.get(`${side.code}|${top.code}`) ?? null };
}

// A product's combinations, laid out, with `cell(row)` drawn for each: the
// number an office has of it, or a box to type how many of it change.
function Entries({ stock, cell }) {
  const layout = useMemo(() => layoutOf(stock), [stock]);
  if (layout === null) {
    return (
      <div className="xo-stock-rows">
        {stock.combinations.map((row) => (
          <div key={row.sku} className="xo-stock-row"><span>{row.label}</span>{cell(row)}</div>
        ))}
      </div>
    );
  }
  const { down, across, at } = layout;
  return (
    <div className="xo-stock-table" role="table" aria-label={`${down.name}, ${across.name}`}
      style={{ '--xo-stock-columns': across.values.length }}>
      {/* What the columns are, over them. */}
      <i aria-hidden="true" /><span className="xo-stock-table-across" aria-hidden="true">{across.name}</span>
      <div role="row">
        <span role="columnheader">{down.name}</span>
        {across.values.map((top) => <span key={top.code} role="columnheader" title={across.name}>{top.label}</span>)}
      </div>
      {down.values.map((side) => (
        <div key={side.code} role="row">
          <span role="rowheader">{side.label}</span>
          {across.values.map((top) => {
            const row = at(side, top);
            return <span key={top.code} role="cell">{row !== null && cell(row)}</span>;
          })}
        </div>
      ))}
    </div>
  );
}

// How many the office has now. `stock`: as XOmics answers it. A product with
// choices is kept per combination of them (a ring in one finish and size):
// the number of all of them together, then each one's, laid out to be read at
// a glance. Choosing one (`onOnly(sku)`) narrows the history beside it to
// that one; choosing it again lets go. `onChange({ adds })` asks for a change;
// left out, the stock is only shown. `onRequest` asks a warehouse for more.
export function StockNow({
  stock, only = null, onOnly, onChange, onRequest = null,
}) {
  return (
    <section className="xo-pm-card xo-stock-now-card">
      <h3>{tx('ui.store.stock.now_title')}</h3>
      <div className="xo-stock-now">
        <p><strong>{stock.stock.toLocaleString()}</strong></p>
        {onChange && <Changes onChange={(adds) => onChange({ adds })} onRequest={onRequest} />}
      </div>
      {stock.stockPerCombination && (
        <Entries stock={stock} cell={(row) => (
          <button type="button" aria-pressed={only === row.sku}
            className={`xo-stock-cell${row.stock === 0 ? ' is-none' : ''}${only === row.sku ? ' is-on' : ''}`}
            aria-label={`${row.label}: ${tx('ui.store.stock.after', { count: row.stock.toLocaleString() })}`}
            onClick={() => onOnly(only === row.sku ? null : row.sku)}>
            {row.stock.toLocaleString()}
          </button>
        )} />
      )}
    </section>
  );
}

// What happened to the stock, newest first, beside the number: every change a
// person made and everything a dispatched shipment took, in one list. It is
// narrowed by words, by kind and to one combination (`only`, its SKU, let go
// with `onOnly(null)`), and older changes are read on request. `stock`: the
// page XOmics answered for what is asked now; `older`: the changes read after
// it; `hasMore`: there are older ones still. `requestPath`: the page that
// opens a request between an office and a warehouse, for the changes that
// came of one (what a warehouse sent, what an office took in).
export function StockHistory({
  stock, older, hasMore, kind, search, only = null, busy, onKind, onSearch, onOnly, onMore, requestPath = null,
}) {
  const one = only === null ? null : stock.combinations.find((row) => row.sku === only) ?? null;
  // What is typed is asked for once the typing pauses.
  const [typed, setTyped] = useState(search);
  useEffect(() => { setTyped(search); }, [search]);
  useEffect(() => {
    if (typed === search) return undefined;
    const waiting = window.setTimeout(() => onSearch(typed), 300);
    return () => window.clearTimeout(waiting);
  }, [onSearch, search, typed]);
  const rows = [...stock.movements, ...older];
  const options = KINDS.map((value) => ({
    value, label: tx(KIND_LABEL[value]), count: stock.counts[value === '' ? 'all' : value],
  }));
  const reference = (order) => (/^[0-9]{16}$/.test(order.reference ?? '') ? orderReferenceLabel(order.reference) : null);
  return (
    <section className="xo-pm-card xo-stock-history" aria-busy={busy}>
      <h3>{tx('ui.store.stock.history')}</h3>
      <div className="xo-stock-history-tools">
        <SearchField value={typed} onChange={setTyped} placeholder={tx('ui.store.stock.search')} />
        <FilterMenu value={kind} onChange={onKind} options={options} label={tx('ui.store.stock.kind')} />
      </div>
      {one !== null && (
        <p className="xo-stock-only">
          <span>{one.label}</span>
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.store.stock.whole_history')} title={tx('ui.store.stock.whole_history')}
            onClick={() => onOnly(null)}><X aria-hidden="true" /></button>
        </p>
      )}
      {rows.length === 0 ? (
        <p className="xo-stock-none">{stock.counts.all === 0 && search === '' ? tx('ui.store.stock.no_history') : tx('ui.store.stock.none_found')}</p>
      ) : (
        <ol>
          {rows.map((row) => {
            // What a request moved opens the request, and what a shipment took
            // opens its order: the whole line is the way there.
            const asked = row.request && requestPath ? row.request : null;
            const order = asked === null && row.order && reference(row.order) ? row.order : null;
            const line = <>
              <b className={row.quantity > 0 ? 'is-added' : 'is-taken'}>{signed(row.quantity)}</b>
              <span className="xo-stock-what">
                <strong>
                  {tx(row.labelKey)}{row.label ? ` · ${row.label}` : ''}
                  {asked && ` · ${tx('ui.store.stock.request', { number: asked.number })}`}
                  {order && ` · ${tx('ui.store.stock.order', { reference: reference(order) })}`}
                </strong>
                <small>{[when(row.at), row.by].filter(Boolean).join(' · ')}</small>
                {row.note && <em dir="auto">{row.note}</em>}
              </span>
              <span className="xo-stock-after">{tx('ui.store.stock.after', { count: row.stockAfter.toLocaleString() })}</span>
              {asked || order ? <ChevronRight aria-hidden="true" /> : <i aria-hidden="true" />}
            </>;
            const to = asked ? `${requestPath}?request=${encodeURIComponent(asked.uuid)}`
              : order ? `/xomics/orders?orderUuid=${encodeURIComponent(order.uuid)}` : null;
            return (
              <li key={row.id}>
                {to ? <Link className="xo-stock-line is-link" to={to}>{line}</Link> : <div className="xo-stock-line">{line}</div>}
              </li>
            );
          })}
        </ol>
      )}
      {hasMore && (
        <button type="button" className="xo-btn-2 xo-stock-more" disabled={busy} onClick={onMore}>{tx('ui.store.stock.more')}</button>
      )}
    </section>
  );
}

// A whole number of one or more, as it is typed; null for anything else.
export const countOf = (typed) => {
  const count = Number(String(typed).trim());
  return String(typed).trim() !== '' && Number.isSafeInteger(count) && count >= 1 ? count : null;
};
// A box left empty, or holding 0, asks for nothing.
export const isLeft = (typed) => String(typed ?? '').trim() === '' || Number(String(typed).trim()) === 0;

// A box for each of a product's combinations, laid out as its stock is, to
// type how many of each (added, taken or sent), and one box that gives them
// all the same number. `amounts`: what is typed, by SKU, handed back whole by
// `onAmounts`; `wrong`: the SKUs whose box is marked; `shows`: an empty box
// shows how many there are of it.
export function EntryBoxes({
  stock, amounts, wrong = [], shows = false, disabled = false, onAmounts,
}) {
  const [same, setSame] = useState('');
  return (
    <div className="xo-stock-each" role="group" aria-labelledby="xo-stock-each-title">
      <div className="xo-stock-each-head">
        <strong id="xo-stock-each-title">{tx('ui.store.stock.how_many_each')}</strong>
        <label className="xo-stock-same">
          <span>{tx('ui.store.stock.same_for_all')}</span>
          <input type="text" inputMode="numeric" value={same} disabled={disabled} onChange={(event) => {
            setSame(event.target.value);
            onAmounts(Object.fromEntries(stock.combinations.map((row) => [row.sku, event.target.value])));
          }} />
        </label>
      </div>
      <Entries stock={stock} cell={(row) => (
        <input type="text" inputMode="numeric" className={`xo-stock-box${wrong.includes(row.sku) ? ' is-wrong' : ''}`}
          aria-label={row.label} aria-invalid={wrong.includes(row.sku)} value={amounts[row.sku] ?? ''} disabled={disabled}
          placeholder={shows ? row.stock.toLocaleString() : ' '}
          onChange={(event) => { setSame(''); onAmounts({ ...amounts, [row.sku]: event.target.value }); }} />
      )} />
    </div>
  );
}

// One change to a stock, in a form of its own: how many, why, a note. `change`:
// whether it adds or takes (`adds`). A product with choices is changed per
// combination, as many at once as arrived or left: a box each, laid out as the
// stock is, and one box that fills them all with the same number. Saved at
// once, all of it or none; `onDone` gets the stock as it is after. `save`: how
// it is saved, when it is not an office's stock (a warehouse's, a shop's).
export function StockChange({
  office, product, stock, change, onClose, onDone, save = null,
}) {
  const reasons = stock.reasons.filter((reason) => reason.adds === change.adds);
  const perEntry = stock.stockPerCombination;
  const [quantity, setQuantity] = useState('');
  // What is typed for each combination, by SKU.
  const [amounts, setAmounts] = useState({});
  const [wrong, setWrong] = useState([]);
  const [reason, setReason] = useState(reasons.length === 1 ? reasons[0].code : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const typed = perEntry ? stock.combinations.filter((row) => !isLeft(amounts[row.sku])) : [];
  const close = useLeave(busy, quantity.trim() !== '' || typed.length > 0 || note.trim() !== '', onClose);
  const title = change.adds ? tx('ui.store.stock.add') : tx('ui.store.stock.remove');
  const inAll = typed.reduce((sum, row) => sum + (countOf(amounts[row.sku]) ?? 0), 0);

  function refuse(message, skus = []) {
    setWrong(skus);
    setError(message);
  }

  // What is asked for, or null once what is wrong with it has been said.
  function asked() {
    if (!perEntry) {
      const count = countOf(quantity);
      if (count === null) { refuse(tx('ui.store.stock.error.quantity')); return null; }
      if (!change.adds && count > stock.stock) { refuse(tx('ui.store.stock.error.not_enough', { count: stock.stock.toLocaleString() })); return null; }
      return { quantity: count };
    }
    if (typed.length === 0) { refuse(tx('ui.store.stock.error.nothing')); return null; }
    const unreadable = typed.filter((row) => countOf(amounts[row.sku]) === null);
    if (unreadable.length > 0) { refuse(tx('ui.store.stock.error.quantity'), unreadable.map((row) => row.sku)); return null; }
    const short = change.adds ? [] : typed.filter((row) => countOf(amounts[row.sku]) > row.stock);
    if (short.length > 0) {
      refuse(tx('ui.store.stock.error.not_enough_of', { count: short[0].stock.toLocaleString(), name: short[0].label }), short.map((row) => row.sku));
      return null;
    }
    return { lines: typed.map((row) => ({ sku: row.sku, quantity: countOf(amounts[row.sku]) })) };
  }

  async function submit(event) {
    event.preventDefault();
    const what = asked();
    if (what === null) return;
    if (reason === '') { refuse(tx('ui.store.stock.error.reason')); return; }
    refuse('');
    setBusy(true);
    const body = { ...what, reasonCode: reason, ...(note.trim() === '' ? {} : { note: note.trim() }) };
    try {
      onDone(await (save ? save(body) : changeOfficeStock(office.id, product.id, body)));
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <form className={`xo-pc-form ${perEntry ? 'is-entries' : 'is-narrow'}`} role="dialog" aria-modal="true" aria-labelledby="xo-stock-form-title"
        onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb is-icon">{change.adds ? <Plus aria-hidden="true" /> : <Minus aria-hidden="true" />}</span>
          <div>
            <h2 id="xo-stock-form-title">{title}</h2>
            <p>{product.name}</p>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
            <X aria-hidden="true" /></button>
        </header>

        <div className="xo-pc-form-body">
          {perEntry ? (
            // Taking: an empty box shows how many there are to take from.
            <EntryBoxes stock={stock} amounts={amounts} wrong={wrong} shows={!change.adds} disabled={busy}
              onAmounts={(next) => { refuse(''); setAmounts(next); }} />
          ) : <>
            <p className="xo-stock-has">{tx('ui.store.stock.now', { count: stock.stock.toLocaleString() })}</p>
            <label className="xo-stock-field">
              <span>{tx('ui.store.stock.how_many')}</span>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <input type="number" min="1" step="1" inputMode="numeric" value={quantity} disabled={busy} autoFocus
                onChange={(event) => setQuantity(event.target.value)} />
            </label>
          </>}
          <fieldset className="xo-stock-reasons" disabled={busy}>
            <legend>{tx('ui.store.stock.why')}</legend>
            {reasons.map((entry) => (
              <label key={entry.code} className={`xo-stock-reason${reason === entry.code ? ' is-on' : ''}`}>
                <input type="radio" name="xo-stock-reason" value={entry.code} checked={reason === entry.code}
                  onChange={() => setReason(entry.code)} />
                <span>{tx(entry.labelKey)}</span>
              </label>
            ))}
          </fieldset>
          <label className="xo-stock-field is-wide">
            <span>{tx('ui.store.stock.note')}</span>
            <input type="text" maxLength={500} value={note} disabled={busy} dir="auto" onChange={(event) => setNote(event.target.value)} />
          </label>
        </div>

        <footer className="xo-pc-form-foot">
          {error
            ? <p className="xo-alert t-bad" role="alert">{error}</p>
            : <span className="xo-stock-sum">{inAll > 0 ? tx('ui.store.stock.in_all', { count: inAll.toLocaleString() }) : ''}</span>}
          <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>{tx('ui.common.cancel')}</button>
          <button type="submit" className="xo-btn" disabled={busy}>{busy ? tx('ui.products.settings.saving') : title}</button>
        </footer>
      </form>
    </div>
  );
}

// Asked when an order's shipment is marked as dispatched (an order's own
// parcel, or a shipment sent to someone without an order, which is kept as a
// free order): what the parcel takes from the stock of the office that sends
// it, line by line. Confirming dispatches it and takes them. A parcel that
// holds more than the office has cannot go until the stock is put right.
export function DispatchStock({ shipmentUuid, onClose, onDispatched }) {
  const [lines, setLines] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const close = useLeave(busy, false, onClose);

  useEffect(() => {
    let live = true;
    loadShipmentStock(shipmentUuid).then((rows) => { if (live) setLines(rows); })
      .catch((caught) => { if (live) { setLines([]); setError(errorMessage(caught)); } });
    return () => { live = false; };
  }, [shipmentUuid]);

  const total = (lines ?? []).reduce((sum, line) => sum + line.quantity, 0);
  const short = (lines ?? []).filter((line) => line.stock < line.quantity);
  const nameOf = (line) => (line.label ? `${line.name} · ${line.label}` : line.name);

  async function confirm(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await dispatchCarrierShipment(shipmentUuid);
      onDispatched();
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  // Over whatever asked for it (an order's window, a shipment's panel): at the
  // portal's own root, so nothing that holds the button can clip it.
  return createPortal(
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <form className="xo-pc-form is-narrow" role="dialog" aria-modal="true" aria-labelledby="xo-dispatch-stock-title"
        onSubmit={confirm} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb is-icon"><Truck aria-hidden="true" /></span>
          <div>
            <h2 id="xo-dispatch-stock-title">{tx('ui.carrier.mark_dispatched')}</h2>
            <p>{lines === null ? tx('ui.store.stock.reading') : tx('ui.store.stock.takes', { count: total.toLocaleString() })}</p>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
            <X aria-hidden="true" /></button>
        </header>

        {lines !== null && lines.length > 0 && (
          <div className="xo-pc-form-body">
            <ul className="xo-stock-takes">
              {lines.map((line) => (
                <li key={`${line.productId}-${line.sku ?? ''}`} className={line.stock < line.quantity ? 'is-short' : undefined}>
                  <b>{line.quantity.toLocaleString()} ×</b>
                  <span>{nameOf(line)}</span>
                  <small>{tx('ui.store.stock.now', { count: line.stock.toLocaleString() })}</small>
                </li>
              ))}
            </ul>
          </div>
        )}

        <footer className="xo-pc-form-foot">
          {error !== '' || short.length > 0
            ? <p className="xo-alert t-bad" role="alert">{error !== '' ? error : tx('ui.store.stock.error.short')}</p> : <span />}
          <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>{tx('ui.common.cancel')}</button>
          <button type="submit" className="xo-btn" disabled={busy || lines === null || short.length > 0}>
            {busy ? tx('ui.carrier.dispatching') : tx('ui.store.stock.confirm_dispatch')}</button>
        </footer>
      </form>
    </div>,
    document.getElementById('xo') ?? document.body,
  );
}
